import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { API_BASE } from '../api';
import Layout from '../components/Layout';
import FotoUpload from '../components/FotoUpload';
import PhotoCarousel from '../components/PhotoCarousel';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmModal';
import { T } from '../styles/theme';
import EbayConnect from '../components/EbayConnect';
import AnnuncioAIModal from '../components/AnnuncioAIModal';
import { useSubscription } from '../hooks/useSubscription';
const CATEGORIES = ['Scarpe', 'Abbigliamento', 'Profumi', 'Tech', 'Borse'];
const CONDITIONS = ['Nuovo', 'Ottime', 'Buone', 'Discrete'];
const PIATTAFORME = ['eBay', 'Vinted', 'Altro'];


export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();
  const isMobile = useIsMobile();
  const [product, setProduct] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showSell, setShowSell] = useState(false);
  const [sellForm, setSellForm] = useState({ prezzo_acquisto: '', prezzo_vendita: '', commissioni: '', piattaforma: 'eBay', note: '' });
  const [selling, setSelling] = useState(false);
  const [sellResult, setSellResult] = useState(null);
  const { isPro } = useSubscription();
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showAnnuncioModal, setShowAnnuncioModal] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [copied, setCopied] = useState('');
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const touchStartRef = useRef(null);
  const [touchDragIdx, setTouchDragIdx] = useState(null);
  const [touchOverIdx, setTouchOverIdx] = useState(null);
  const thumbRefs = useRef([]);

  const loadProduct = () => {
    api.get(`/products/${id}`).then((res) => {
      setProduct(res.data);
      setForm({ name: res.data.name, description: res.data.description, price: res.data.price, quantity: res.data.quantity, category: res.data.category, condition: res.data.condition, notes: res.data.notes });
    }).catch(() => navigate('/products'));
  };

  useEffect(() => { loadProduct(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const sellPreview = useMemo(() => {
    const pv = parseFloat(sellForm.prezzo_vendita) || 0;
    const pa = parseFloat(sellForm.prezzo_acquisto) || 0;
    const comm = parseFloat(sellForm.commissioni) || 0;
    const profitto = +(pv - pa - comm).toFixed(2);
    const roi = pa > 0 ? +((profitto / pa) * 100).toFixed(1) : 0;
    return { comm, profitto, roi };
  }, [sellForm.prezzo_vendita, sellForm.prezzo_acquisto, sellForm.commissioni]);

  const handleUpdate = async (e) => {
    e.preventDefault(); setError('');
    try { await api.put(`/products/${id}`, { ...form, price: parseFloat(form.price), quantity: parseInt(form.quantity) || 0 }); setEditing(false); loadProduct(); toast.success('Prodotto aggiornato'); }
    catch (err) { setError(err.response?.data?.detail || 'Errore aggiornamento'); }
  };

  const handleDelete = async () => {
    const ok = await confirm('Sei sicuro di voler eliminare questo prodotto?');
    if (!ok) return;
    setDeleting(true);
    try { await api.delete(`/products/${id}`); toast.success('Prodotto eliminato'); navigate('/products'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); setDeleting(false); }
  };

  const handleDeletePhoto = async (photoId) => {
    const ok = await confirm('Eliminare questa foto?');
    if (!ok) return;
    try { await api.delete(`/products/${id}/foto/${photoId}`); setActivePhoto(0); loadProduct(); toast.success('Foto eliminata'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleSell = async (e) => {
    e.preventDefault(); setSelling(true);
    try {
      const res = await api.post(`/products/${id}/vendi`, { prezzo_vendita: parseFloat(sellForm.prezzo_vendita), prezzo_acquisto: parseFloat(sellForm.prezzo_acquisto), commissioni: parseFloat(sellForm.commissioni) || 0, piattaforma: sellForm.piattaforma, note: sellForm.note });
      setSellResult(res.data); loadProduct(); toast.success('Vendita registrata!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore vendita'); } finally { setSelling(false); }
  };

  const handleRevert = async () => {
    const ok = await confirm('Riportare questo prodotto in inventario?');
    if (!ok) return;
    try { await api.post(`/products/${id}/disponibile`); loadProduct(); setSellResult(null); toast.success('Prodotto riportato in inventario'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleAiAnnuncio = async () => {
    setAiLoading(true);
    try { const res = await api.post(`/products/${id}/ai-annuncio`); setAiResult(res.data); }
    catch (err) { toast.error(err.response?.data?.detail || 'Errore generazione AI'); } finally { setAiLoading(false); }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  if (!product) return (
    <Layout>
      <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 rounded-full" style={{ border: `2px solid ${T.violetDark}`, borderTopColor: 'transparent' }}></div></div>
    </Layout>
  );

  const photos = product.photos || [];
  const isSold = product.status === 'venduto';

  return (
    <Layout>
      {ConfirmDialog}

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(13,11,30,0.95)', backdropFilter: 'blur(8px)' }} onClick={() => setLightbox(null)}>
          <img src={`${API_BASE}${photos[lightbox]?.foto_url}`} alt="" className="max-w-[90vw] max-h-[90vh] object-contain" style={{ borderRadius: 12 }} />
          <button className="absolute top-6 right-6 text-3xl" style={{ color: T.text }} onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}

      {/* AI Modal */}
      {aiResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(13,11,30,0.95)', backdropFilter: 'blur(8px)' }} onClick={() => setAiResult(null)}>
          <div className="card max-w-[600px] w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="section-title" style={{ color: T.violet }}>🤖 Annuncio generato con AI</h3>
              <button onClick={() => setAiResult(null)} className="text-xl" style={{ color: T.muted }}>✕</button>
            </div>
            <div className="space-y-3">
              {/* Titolo */}
              <div>
                <label className="label">Titolo</label>
                <div className="flex items-center gap-2 mt-1 p-3 rounded-lg" style={{ backgroundColor: T.elem, border: `1px solid ${T.border}` }}>
                  <p className="flex-1 font-bold" style={{ color: T.text }}>{aiResult.titolo}</p>
                  <button onClick={() => copyText(aiResult.titolo, 'titolo')} className="btn-ghost text-xs shrink-0">
                    {copied === 'titolo' ? '✓ Copiato!' : '📋 Copia'}
                  </button>
                </div>
              </div>
              {/* Descrizione */}
              <div>
                <label className="label">Descrizione</label>
                <div className="flex items-start gap-2 mt-1 p-3 rounded-lg" style={{ backgroundColor: T.elem, border: `1px solid ${T.border}` }}>
                  <p className="flex-1 text-sm leading-relaxed" style={{ color: T.text }}>{aiResult.descrizione}</p>
                  <button onClick={() => copyText(aiResult.descrizione, 'desc')} className="btn-ghost text-xs shrink-0">
                    {copied === 'desc' ? '✓ Copiato!' : '📋 Copia'}
                  </button>
                </div>
              </div>
              {/* Prezzo + Categoria */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Prezzo consigliato</label>
                  <p className="text-lg font-bold mt-1" style={{ color: T.green }}>€{aiResult.prezzo_consigliato}</p>
                </div>
                <div>
                  <label className="label">Categoria</label>
                  <p className="mt-1"><span className="badge badge-violet">{aiResult.categoria_vinted || aiResult.categoria}</span></p>
                </div>
              </div>
              {/* Hashtag */}
              {aiResult.hashtag?.length > 0 && (
                <div>
                  <label className="label">Hashtag</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {aiResult.hashtag.map((t, i) => (
                      <button key={i} onClick={() => copyText(`#${t}`, `tag${i}`)} className="text-xs transition-colors rounded-full px-3 py-1"
                        style={{ backgroundColor: T.elem, border: `1px solid ${T.border}`, color: copied === `tag${i}` ? T.green : T.violet }}>
                        #{t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {/* Copia tutto */}
              <button onClick={() => {
                const all = `${aiResult.titolo}\n\n${aiResult.descrizione}\n\nPrezzo: €${aiResult.prezzo_consigliato}\n\n${(aiResult.hashtag || []).map(t => '#' + t).join(' ')}`;
                copyText(all, 'all');
              }} className="btn-primary w-full mt-2">
                {copied === 'all' ? '✓ Copiato tutto!' : '📋 Copia Tutto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 1100, margin: '0 auto 24px', padding: '32px 16px 0' }}>
        <button onClick={() => navigate('/products')} style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: '#1c1640', border: '1px solid #2d1b69', borderRadius: 9999,
          padding: '10px 20px', color: '#e2e8f0', fontFamily: 'DM Sans, sans-serif',
          fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.color = '#a78bfa'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#2d1b69'; e.currentTarget.style.color = '#e2e8f0'; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Torna all'Inventario
        </button>
        {isSold
          ? <span style={{ background: 'rgba(248,113,113,0.15)', border: '1px solid #f87171', borderRadius: 9999, padding: '6px 14px', fontSize: '0.75rem', fontWeight: 700, color: '#f87171', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>● Venduto</span>
          : <span style={{ background: 'rgba(74,222,128,0.15)', border: '1px solid #4ade80', borderRadius: 9999, padding: '6px 14px', fontSize: '0.75rem', fontWeight: 700, color: '#4ade80', fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase' }}>● Disponibile</span>
        }
      </div>

      <div className="max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-9 gap-8">
          {/* LEFT — Gallery (45%) */}
          <div className="lg:col-span-4 space-y-3">
            {/* Main photo */}
            <div className="w-full overflow-hidden" style={{ aspectRatio: '1/1', borderRadius: 16, border: `1px solid ${T.border}`, boxShadow: T.shadow }}>
              {photos.length > 0 ? (
                <img src={`${API_BASE}${photos[activePhoto]?.foto_url}`} alt={product.name}
                  className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(activePhoto)} />
              ) : product.foto_url ? (
                <img src={`${API_BASE}${product.foto_url}`} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center" style={{ backgroundColor: T.elem }}>
                  <span className="text-5xl opacity-30">📦</span>
                  <span className="text-sm mt-2" style={{ color: T.muted }}>Nessuna foto</span>
                </div>
              )}
            </div>

            {/* Thumbnails with drag & drop reorder (desktop) + touch reorder (mobile) */}
            {photos.length > 1 && (
              <div>
                {/* Mobile: bottoni sposta su/giu per la foto attiva */}
                {isMobile && !isSold && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    marginBottom: 8, padding: '6px 0',
                  }}>
                    <button
                      disabled={activePhoto === 0}
                      onClick={async () => {
                        const newPhotos = [...photos];
                        const [moved] = newPhotos.splice(activePhoto, 1);
                        newPhotos.splice(activePhoto - 1, 0, moved);
                        setActivePhoto(activePhoto - 1);
                        try { await api.post(`/products/${id}/foto/reorder`, { order: newPhotos.map(p => p.id) }); loadProduct(); } catch {}
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 10,
                        background: activePhoto === 0 ? T.elem : 'rgba(124,58,237,0.15)',
                        border: `1px solid ${activePhoto === 0 ? T.border : T.violetDark}`,
                        color: activePhoto === 0 ? T.muted : T.violet,
                        fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600,
                        cursor: activePhoto === 0 ? 'not-allowed' : 'pointer',
                        opacity: activePhoto === 0 ? 0.4 : 1,
                        transition: '0.15s',
                      }}>
                      ← Sposta a sinistra
                    </button>
                    <span style={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.muted }}>
                      {activePhoto + 1}/{photos.length}
                    </span>
                    <button
                      disabled={activePhoto === photos.length - 1}
                      onClick={async () => {
                        const newPhotos = [...photos];
                        const [moved] = newPhotos.splice(activePhoto, 1);
                        newPhotos.splice(activePhoto + 1, 0, moved);
                        setActivePhoto(activePhoto + 1);
                        try { await api.post(`/products/${id}/foto/reorder`, { order: newPhotos.map(p => p.id) }); loadProduct(); } catch {}
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 10,
                        background: activePhoto === photos.length - 1 ? T.elem : 'rgba(124,58,237,0.15)',
                        border: `1px solid ${activePhoto === photos.length - 1 ? T.border : T.violetDark}`,
                        color: activePhoto === photos.length - 1 ? T.muted : T.violet,
                        fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600,
                        cursor: activePhoto === photos.length - 1 ? 'not-allowed' : 'pointer',
                        opacity: activePhoto === photos.length - 1 ? 0.4 : 1,
                        transition: '0.15s',
                      }}>
                      Sposta a destra →
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {photos.map((photo, idx) => (
                    <div key={photo.id} className="relative group"
                      draggable={!isSold && !isMobile}
                      onDragStart={() => setDragIdx(idx)}
                      onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                      onDragLeave={() => setDragOverIdx(null)}
                      onDrop={async () => {
                        if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
                        const newPhotos = [...photos];
                        const [moved] = newPhotos.splice(dragIdx, 1);
                        newPhotos.splice(idx, 0, moved);
                        setDragIdx(null); setDragOverIdx(null); setActivePhoto(idx);
                        try { await api.post(`/products/${id}/foto/reorder`, { order: newPhotos.map(p => p.id) }); loadProduct(); } catch {}
                      }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                      onClick={() => setActivePhoto(idx)}
                      className="overflow-hidden transition-all"
                      style={{
                        width: 72, height: 72, borderRadius: 8,
                        cursor: isSold ? 'pointer' : (isMobile ? 'pointer' : 'grab'),
                        opacity: dragIdx === idx ? 0.4 : 1,
                        border: dragOverIdx === idx ? `2px solid ${T.violetDark}` : idx === activePhoto ? `2px solid ${T.violet}` : '2px solid transparent',
                        boxShadow: idx === activePhoto ? `0 0 8px ${T.violetGlow}` : 'none',
                        backgroundColor: dragOverIdx === idx ? T.violetGlow : 'transparent',
                      }}>
                      <img src={`${API_BASE}${photo.foto_url}`} alt="" className="w-full h-full object-cover" draggable={false} />
                      {idx === 0 && <span className="absolute top-0.5 left-0.5 text-[9px] font-bold px-1 py-0.5 rounded text-white" style={{ backgroundColor: T.violet }}>Cover</span>}
                      {!isSold && !isMobile && (
                        <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'rgba(13,11,30,0.9)', color: T.muted }}>Trascina</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photo management */}
            {!isSold && (
              <div className="flex gap-3">
                {photos.length < 20 && (
                  <div className="flex-1">
                    <FotoUpload productId={product.id} onUploaded={loadProduct}
                      buttonLabel={photos.length === 0 ? '📷 Trascina le foto qui oppure clicca per selezionare' : `📷 Aggiungi Foto (${photos.length}/20)`} />
                  </div>
                )}
                {photos.length > 0 && (
                  <button onClick={() => handleDeletePhoto(photos[activePhoto]?.id)} className="btn-danger text-sm self-start">
                    Elimina foto
                  </button>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — Details (55%) */}
          <div className="lg:col-span-5">
            {sellResult && (
              <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: T.greenGlow, border: `1px solid ${T.green}33` }}>
                <h3 className="font-bold text-lg mb-3" style={{ color: T.green }}>Vendita Completata!</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span style={{ color: T.muted }}>Acquistato:</span> <span className="font-medium" style={{ color: T.text }}>€{sellResult.prezzo_acquisto.toFixed(2)}</span></div>
                  <div><span style={{ color: T.muted }}>Venduto:</span> <span className="font-medium" style={{ color: T.text }}>€{sellResult.prezzo_vendita.toFixed(2)}</span></div>
                  <div><span style={{ color: T.muted }}>Commissioni:</span> <span className="font-medium" style={{ color: T.red }}>-€{sellResult.commissioni.toFixed(2)}</span></div>
                  <div><span style={{ color: T.muted }}>Profitto netto:</span> <span className="font-bold" style={{ color: sellResult.profitto_netto >= 0 ? T.green : T.red }}>€{sellResult.profitto_netto.toFixed(2)}</span></div>
                </div>
              </div>
            )}

            {showSell && !isSold && !sellResult && (
              <div className="card p-6 mb-6">
                <h3 className="section-title mb-4" style={{ color: T.violet }}>Segna come Venduto</h3>
                <form onSubmit={handleSell} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="label">Prezzo acquisto €</label>
                      <input type="number" step="0.01" required value={sellForm.prezzo_acquisto} onChange={(e) => setSellForm({ ...sellForm, prezzo_acquisto: e.target.value })} className="input" /></div>
                    <div><label className="label">Prezzo vendita €</label>
                      <input type="number" step="0.01" required value={sellForm.prezzo_vendita} onChange={(e) => setSellForm({ ...sellForm, prezzo_vendita: e.target.value })} className="input" /></div>
                  </div>
                  <div><label className="label">Commissioni €</label>
                    <input type="number" step="0.01" value={sellForm.commissioni} onChange={(e) => setSellForm({ ...sellForm, commissioni: e.target.value })} className="input" placeholder="0.00" /></div>
                  <div><label className="label">Piattaforma</label>
                    <select value={sellForm.piattaforma} onChange={(e) => setSellForm({ ...sellForm, piattaforma: e.target.value })} className="input">
                      {PIATTAFORME.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select></div>
                  <div><label className="label">Note</label>
                    <textarea value={sellForm.note} onChange={(e) => setSellForm({ ...sellForm, note: e.target.value })} rows={2} className="input" /></div>
                  {(sellForm.prezzo_vendita || sellForm.prezzo_acquisto) && (
                    <div className="rounded-lg p-4" style={{ backgroundColor: T.elem, border: `1px solid ${sellPreview.profitto >= 0 ? `${T.green}33` : `${T.red}33`}` }}>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between"><span style={{ color: T.muted }}>Commissioni:</span><span style={{ color: T.red }}>-€{sellPreview.comm.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span style={{ color: T.muted }}>Profitto netto:</span><span className="font-bold text-lg" style={{ color: sellPreview.profitto >= 0 ? T.green : T.red }}>€{sellPreview.profitto.toFixed(2)}</span></div>
                        <div className="flex justify-between"><span style={{ color: T.muted }}>ROI:</span><span className="font-bold" style={{ color: sellPreview.roi >= 0 ? T.green : T.red }}>{sellPreview.roi}%</span></div>
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button type="submit" disabled={selling} className="btn-primary flex-1 py-3 disabled:opacity-50">
                      {selling ? 'Elaborazione...' : 'Conferma Vendita'}</button>
                    <button type="button" onClick={() => setShowSell(false)} className="btn-secondary px-5 py-3">Annulla</button>
                  </div>
                </form>
              </div>
            )}

            {!editing && !showSell && (
              <div className="space-y-4">
                {/* Name + chips */}
                <div>
                  <h1 className="font-bold mb-1" style={{ fontSize: '1.6rem', color: T.text }}>{product.name}</h1>
                  <div className="flex gap-2 flex-wrap">
                    {product.brand && <span className="badge badge-violet">{product.brand}</span>}
                    {product.category && <span className="badge" style={{ backgroundColor: T.elem, border: `1px solid ${T.border}`, color: T.text }}>{product.category}</span>}
                  </div>
                </div>

                {/* Price */}
                <div>
                  <p className="font-bold" style={{ fontSize: '2rem', color: T.violet }}>€{(product.price || 0).toFixed(2)}</p>
                  {product.prezzo_acquisto != null && <p style={{ color: T.muted, fontSize: '0.9rem' }}>Acquistato a €{(product.prezzo_acquisto || 0).toFixed(2)}</p>}
                </div>

                <div className="divider" />

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {[
                    product.condition && { label: 'Condizione', value: product.condition },
                    product.taglia && { label: 'Taglia', value: product.taglia },
                    product.colore && { label: 'Colore', value: product.colore },
                    { label: 'Quantita', value: product.quantity },
                    { label: 'Data aggiunta', value: new Date(product.created_at).toLocaleDateString('it-IT') },
                  ].filter(Boolean).map((row, i) => (
                    <div key={i}>
                      <p className="uppercase" style={{ color: T.muted, fontSize: '0.8rem' }}>{row.label}</p>
                      <p style={{ color: T.text, fontSize: '0.95rem' }}>{row.value}</p>
                    </div>
                  ))}
                </div>

                {/* Description */}
                {product.description && (
                  <div className="p-3.5 rounded-lg" style={{ backgroundColor: T.elem, border: `1px solid ${T.border}` }}>
                    <p className="text-sm leading-relaxed" style={{ color: T.text }}>{product.description}</p>
                  </div>
                )}

                {/* Private notes */}
                {product.notes && (
                  <div className="p-3.5 rounded-lg" style={{ backgroundColor: T.elem, border: `1px solid ${T.border}` }}>
                    <p className="text-sm" style={{ color: T.text }}>🔒 {product.notes}</p>
                  </div>
                )}

                {/* Sold summary */}
                {isSold && product.prezzo_acquisto != null && (
                  <div className="rounded-xl p-4" style={{ backgroundColor: T.greenGlow, border: `1px solid ${T.green}33` }}>
                    <h3 className="text-sm font-medium mb-3" style={{ color: T.green }}>Riepilogo Vendita</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span style={{ color: T.muted }}>Acquistato:</span> <span className="font-medium" style={{ color: T.text }}>€{(product.prezzo_acquisto || 0).toFixed(2)}</span></div>
                      <div><span style={{ color: T.muted }}>Venduto:</span> <span className="font-medium" style={{ color: T.text }}>€{(product.prezzo_vendita || 0).toFixed(2)}</span></div>
                      <div><span style={{ color: T.muted }}>Data:</span> <span style={{ color: T.text }}>{product.data_vendita ? new Date(product.data_vendita).toLocaleDateString('it-IT') : '-'}</span></div>
                      <div><span style={{ color: T.muted }}>Profitto:</span> <span className="font-bold" style={{ color: ((product.prezzo_vendita || 0) - (product.prezzo_acquisto || 0)) >= 0 ? T.green : T.red }}>€{((product.prezzo_vendita || 0) - (product.prezzo_acquisto || 0)).toFixed(2)}</span></div>
                    </div>
                  </div>
                )}

                {/* eBay link */}
                {product.ebay_url && (
                  <div className="p-3.5 rounded-lg flex items-center justify-between" style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)' }}>
                    <span style={{ color: T.green, fontSize: '0.85rem', fontWeight: 600 }}>Pubblicato su eBay</span>
                    <a href={product.ebay_url} target="_blank" rel="noopener noreferrer" style={{ color: T.violet, fontSize: '0.8rem', textDecoration: 'underline' }}>Vedi annuncio</a>
                  </div>
                )}

                {/* eBay connect */}
                {!isSold && <EbayConnect compact />}

                {/* Actions box */}
                <div className="p-5 rounded-xl" style={{ backgroundColor: T.elem, border: `1px solid ${T.border}`, marginTop: 20 }}>
                  <p className="uppercase mb-3" style={{ color: T.muted, fontSize: '0.8rem' }}>Azioni rapide</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button onClick={() => {
                      localStorage.setItem('analisi_prefill', JSON.stringify({ nome: product.name, categoria: product.category, descrizione: product.description, foto_urls: photos.map(ph => ph.foto_url), product_id: product.id }));
                      navigate('/analisi-mercato', {
                        state: {
                          prodotto_id: product.id,
                          nome: product.name,
                          categoria: product.category || '',
                          brand: product.brand || '',
                          foto_urls: photos.map(ph => ph.foto_url),
                          from_product: true
                        }
                      });
                    }} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 20px', borderRadius: 24, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                      background: '#1c1640', border: '1px solid #7c3aed', color: '#a78bfa',
                      cursor: 'pointer', transition: '0.15s',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(124,58,237,0.15)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,0.2)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#1c1640'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = ''; }}
                      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'} onMouseUp={(e) => e.currentTarget.style.transform = ''}>
                      📊 Analizza Mercato
                    </button>
                    <button onClick={() => { isPro ? setShowAnnuncioModal(true) : navigate('/abbonamento'); }} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 20px', borderRadius: 24, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                      background: isPro ? 'linear-gradient(135deg,#7c3aed,#4f46e5)' : '#1c1640', border: isPro ? 'none' : '1px solid #2d1f6e', color: isPro ? 'white' : '#6b7280',
                      cursor: 'pointer', transition: '0.15s',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(124,58,237,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = ''; }}
                      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'} onMouseUp={(e) => e.currentTarget.style.transform = ''}>
                      {isPro ? '🤖 Crea Annuncio AI' : '🔒 Annuncio AI (PRO)'}
                    </button>
                    {!isSold ? (
                      <button onClick={() => setShowSell(true)} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '12px 20px', borderRadius: 24, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                        background: 'rgba(74,222,128,0.1)', border: '1px solid #4ade80', color: '#4ade80',
                        cursor: 'pointer', transition: '0.15s',
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(74,222,128,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(74,222,128,0.1)'; e.currentTarget.style.transform = ''; }}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'} onMouseUp={(e) => e.currentTarget.style.transform = ''}>
                        ✅ Segna Venduto
                      </button>
                    ) : (
                      <button onClick={handleRevert} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        padding: '12px 20px', borderRadius: 24, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                        background: 'rgba(251,191,36,0.1)', border: '1px solid #fbbf24', color: '#fbbf24',
                        cursor: 'pointer', transition: '0.15s',
                      }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(251,191,36,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(251,191,36,0.1)'; e.currentTarget.style.transform = ''; }}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'} onMouseUp={(e) => e.currentTarget.style.transform = ''}>
                        ↩️ Torna in Inventario
                      </button>
                    )}
                    <button onClick={() => setEditing(true)} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 20px', borderRadius: 24, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                      background: '#1c1640', border: '1px solid #2d1b69', color: '#e2e8f0',
                      cursor: 'pointer', transition: '0.15s',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a78bfa'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2d1b69'; e.currentTarget.style.transform = ''; }}
                      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'} onMouseUp={(e) => e.currentTarget.style.transform = ''}>
                      ✏️ Modifica
                    </button>
                  </div>
                  <button onClick={handleDelete} disabled={deleting} style={{
                    width: '100%', marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '12px 20px', borderRadius: 24, fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem',
                    background: 'rgba(248,113,113,0.08)', border: '1px solid #f87171', color: '#f87171',
                    cursor: deleting ? 'not-allowed' : 'pointer', transition: '0.15s', opacity: deleting ? 0.5 : 1,
                  }}
                    onMouseEnter={(e) => { if (!deleting) { e.currentTarget.style.background = 'rgba(248,113,113,0.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; e.currentTarget.style.transform = ''; }}
                    onMouseDown={(e) => { if (!deleting) e.currentTarget.style.transform = 'scale(0.97)'; }} onMouseUp={(e) => e.currentTarget.style.transform = ''}>
                    {deleting ? 'Eliminazione...' : '🗑️ Elimina Prodotto'}
                  </button>
                </div>
              </div>
            )}

            {editing && !isSold && (
              <form onSubmit={handleUpdate} className="space-y-4">
                <h2 className="section-title" style={{ color: T.text }}>Modifica Prodotto</h2>
                {error && <div className="text-sm rounded-lg px-4 py-3" style={{ backgroundColor: T.redGlow, border: `1px solid ${T.red}4d`, color: T.red }}>{error}</div>}
                <div><label className="label">Nome *</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="input" /></div>
                <div><label className="label">Descrizione</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label">Prezzo € *</label><input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="input" /></div>
                  <div><label className="label">Quantita *</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required className="input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="label">Categoria</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input"><option value="">Seleziona...</option>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                  <div><label className="label">Condizione</label><select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="input"><option value="">Seleziona...</option>{CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="submit" className="btn-primary px-6 py-2.5">Salva</button>
                  <button type="button" onClick={() => { setEditing(false); setError(''); }} className="btn-secondary px-6 py-2.5">Annulla</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      {showAnnuncioModal && product && (
        <AnnuncioAIModal product={product} onClose={() => setShowAnnuncioModal(false)} />
      )}
    </Layout>
  );
}
